import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import compose from 'recompose/compose';
import { withStyles, Link, Grid } from '@material-ui/core';
import queryString from 'query-string';
import get from 'lodash/get';
import { number, bool, func, shape, arrayOf, string } from 'prop-types';

import { fetchKeyDeploymentList } from '../../../../actions/keyStore';
import { getUserDetails } from '../../../../reducers/portalConfig';
import { hasAdminRole } from '../../../../utils';
import {
  getKeyDeploymentsListTotalPages,
  getKeyDeploymentsListTotalElements,
  getKeyDeploymentsListResults,
} from '../../../../reducers/keyStore';
import styles from './styles';
import ListContainer from '../../../list';
import {
  GRID_ROWS_PER_PAGE_OPTIONS,
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  ALL,
  getKeyDeploymentStatusFilterBy,
 } from '../../../../constants';
import {
  FilterByName,
  FilterByApiKey,
  FilterByKeyStatus,
  SortBy,
  FilterAndSortSeparator,
} from './controls';
import { FilterByDeploymentType } from '../../../../pageComponents';
import { getI18n, getI18nFormattedMessage } from '../../../../utils/intl';
import getDateTime, { getTimeZoneAbbr } from '../../../../utils/datetime';


const getLinkContent = (value, uuid) => (
  <Link underline="none" href={`/admin/console/applications/details/${uuid}/keys`}>
    {value}
  </Link>
);

const LIST_COLUMNS = [{
    id: 'apiKeyName',
    secondaryId: 'applicationUuid',
    label: getI18nFormattedMessage('label.api.keys.name'),
    minWidth: 100,
    format: (value, secondaryValue) => getLinkContent(value, secondaryValue),
  },{
    id: 'apiKey',
    label: getI18nFormattedMessage('label.api.keys.api.key'),
    minWidth: 100,
  },{
    id: 'apiKeyStatus',
    label: getI18nFormattedMessage('label.api.keys.status'),
    minWidth: 100,
    isStatusIcon: true,
  },{
    id: 'apiKeyDeploymentStatus',
    label: getI18nFormattedMessage('label.api.keys.deployment.status'),
    minWidth: 100,
    isStatusIcon: true,
  },{
    id: 'lastTimeDeployed',
    label: getI18nFormattedMessage('label.api.date.last.deployed', { zone: getTimeZoneAbbr() }),
    format: value => getDateTime(value),
    minWidth: 100,
  },
];

export const ListRaw = (props) => {
  const {
    uuid,
    classes,
    isLoading,
    userContext,
    totalPages,
    totalElements = 0,
    results = [],
    isCurrentTab,
  } = props;

  const intl = getI18n(useIntl());
  const parsed = queryString.parse(props.location.search);
  const deploymentStatusFilterOptions = getKeyDeploymentStatusFilterBy(intl);
  const getDeploymentStatus = status =>
    deploymentStatusFilterOptions.filter(s => status.includes(s.uuid));
    const deploymentStatus = parsed.status ? getDeploymentStatus(parsed.status): [];
  const filterSearch = parsed.status ? true : false;

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [filterByName, setFilterByName] = useState('');
  const [filterByapiKey, setFilterByapiKey] = useState('');
  const [filterByStatus, setFilterByStatus] = useState(ALL);
  const [sortBy, setSortBy] = useState('lastTimeDeployed,DESC');
  const [filterByDeploymentType, setFilterByDeploymentType] = useState(deploymentStatus);
  const [filterByAnyApplied, setFilterByAnyApplied] = useState(filterSearch);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);

  const emptyFunction = () => {};

  useEffect(() => {
    if (!hasAdminRole(userContext)) { props.push('/404'); }
  }, [userContext]);

  useEffect(() => {
    if (uuid && isCurrentTab) {
      props.fetchKeyDeploymentList({
        name: filterByName,
        apiKey: filterByapiKey,
        deploymentType: filterByDeploymentType,
        status: filterByStatus,
        sortBy: sortBy,
        page: 0,
        size: rowsPerPage,
        uuid: uuid,
      });
    }
  }, [uuid, isCurrentTab]);

  useEffect(() => {
    const parsed = queryString.parse(props.location.search);
    if (uuid && isCurrentTab && get(parsed, 'status')) {
      const deploymentStatus = parsed.status ? getDeploymentStatus(parsed.status): [];
      const filterSearch = parsed.status ? true : false;

      setFilterByDeploymentType(deploymentStatus);
      setFilterByAnyApplied(filterSearch);
      props.fetchKeyDeploymentList({
        name: filterByName,
        apiKey: filterByapiKey,
        deploymentType: deploymentStatus,
        status: filterByStatus,
        sortBy: sortBy,
        page: 0,
        size: rowsPerPage,
        uuid: uuid,
      });
    }
  }, [props.location, isCurrentTab]);


  const onFilterByNameChange = (value) => {
    setFilterByAnyApplied(true);
    setFilterByName(value);
  };

  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setPage(0);
      props.fetchKeyDeploymentList({
        name: filterByName,
        apiKey: filterByapiKey,
        deploymentType: filterByDeploymentType,
        status: filterByStatus,
        sortBy: sortBy,
        page: 0,
        size: rowsPerPage,
        uuid: uuid,
      });
    }
  };

  const onFilterByDeploymentChange = (newStatus) => {
    if(newStatus.some(status => status.uuid === ALL)) {
      newStatus = []
    }
    setFilterByAnyApplied(true);
    setFilterByDeploymentType(newStatus);
    setPage(0);
    props.fetchKeyDeploymentList({
      name: filterByName,
      apiKey: filterByapiKey,
      status: filterByStatus,
      sortBy: sortBy,
      deploymentType: newStatus,
      page: 0,
      size: rowsPerPage,
      uuid: uuid,
    });
  };

  const onFilterByStatus = (value) => {
    setFilterByAnyApplied(true);
    setFilterByStatus(value);
    setPage(0);
    props.fetchKeyDeploymentList({
      name: filterByName,
      apiKey: filterByapiKey,
      deploymentType: filterByDeploymentType,
      sortBy: sortBy,
      status: value,
      page: 0,
      size: rowsPerPage,
      uuid: uuid,
    });
  };

  const onFilterByApiKey = (value) => {
    setFilterByAnyApplied(true);
    setFilterByapiKey(value);
  };

  const onFilterByApiKeyPress = (e) => {
    if (e.key === 'Enter') {
      setFilterByAnyApplied(true);
      setPage(0);
      props.fetchKeyDeploymentList({
        name: filterByName,
        apiKey: filterByapiKey,
        deploymentType: filterByDeploymentType,
        sortBy: sortBy,
        status: filterByStatus,
        page: 0,
        size: rowsPerPage,
        uuid: uuid,
      });
    }
  };

  const onSortBy = (value) => {
    setFilterByAnyApplied(true);
    setSortBy(value);
    setPage(0);
    props.fetchKeyDeploymentList({
      name: filterByName,
      apiKey: filterByapiKey,
      deploymentType: filterByDeploymentType,
      sortBy: value,
      status: filterByStatus,
      page: 0,
      size: rowsPerPage,
      uuid: uuid,
    });
  };

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    props.fetchKeyDeploymentList({
      name: filterByName,
      apiKey: filterByapiKey,
      deploymentType: filterByDeploymentType,
      status: filterByStatus,
      sortBy: sortBy,
      page: newPage,
      size: rowsPerPage,
      uuid: uuid,
    });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    props.fetchKeyDeploymentList({
      name: filterByName,
      apiKey: filterByapiKey,
      deploymentType: filterByDeploymentType,
      status: filterByStatus,
      sortBy: sortBy,
      page: 0,
      size: newRowsPerPage,
      uuid: uuid,
    });
  };

  const noResultsMessage = filterByAnyApplied ?
    intl.getI18nMessage('label.key.deployments.filter.no.results') :
    intl.getI18nMessage('label.key.deployments.no.results');

  return (
    <ListContainer
      listPageId="key-store-key-deployments-list"
      isLoading={isLoading}
      notificationId="key-store-key-deployments-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationStatus}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      pageClass={classes.listContent}
      pageBodyClass={classes.pageBodyClass}
      pageFilterAndSortClass={classes.pageFilterAndSortClass}
      pageHeaderTitle={''}
      pageHeaderDescription={''}
      pageHeaderTooltipTitle={''}
      addButtonLabel={intl.getI18nMessage('label.key.store.add.button')}
      onAdd={() => {}}
      showListHeader={true}
      filterAndSortContent={(
        <>
          <FilterByName
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.filter')}
            value={filterByName}
            placeholder={intl.getI18nMessage('label.api.keys.name')}
            handleChange={onFilterByNameChange}
            onKeyPress={onFilterByNameKeyPress}
          />
          <FilterByApiKey
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.filter')}
            value={filterByapiKey}
            placeholder={intl.getI18nMessage('label.api.keys.api.key.placeholder')}
            handleChange={onFilterByApiKey}
            onKeyPress={onFilterByApiKeyPress}
            hideLabel
          />
          <FilterByKeyStatus
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.filter')}
            value={filterByStatus}
            placeholder={intl.getI18nMessage('label.key.store.deployments.type.all')}
            handleChange={onFilterByStatus}
            hideLabel
          />
           <Grid item md={2}>
            <FilterByDeploymentType
              fieldContainerClass={classes.fieldContainer}
              name={intl.getI18nMessage('label.filter')}
              selectFieldClass={classes.selectField}
              value={filterByDeploymentType}
              deploymentStatusFilterOptions={deploymentStatusFilterOptions}
              handleChange={onFilterByDeploymentChange}
              data={getKeyDeploymentStatusFilterBy(intl)}
              hideLabel
            />
          </Grid>
          <FilterAndSortSeparator />
          <SortBy
            fieldContainerClass={classes.fieldContainer}
            name={intl.getI18nMessage('label.sort')}
            value={sortBy}
            handleChange={onSortBy}
          />
        </>
     )}
      columns={LIST_COLUMNS}
      rows={results}
      noResultsMessage={noResultsMessage}
      page={page}
      totalElements={totalElements}
      totalPages={totalPages}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onChangePage={onChangePage}
      onChangePreviousPage={onChangePreviousPage}
      onChangeNextPage={onChangeNextPage}
      notifyMessages={emptyFunction}
    />
  );
};

ListRaw.propTypes = {
  classes: shape({}),
  userContext: shape({}),
  isLoading: bool,
  totalPages: number,
  totalElements: number,
  results: arrayOf(shape({})),
  fetchKeyDeploymentList: func,
  resetApp: func,
  notifyMessages: func,
  push: func,
  uuid: string,
  isCurrentTab: bool,
  location: shape({}),
};

const mapStateToProps = (state) => ({
  isLoading: false,
  totalPages: getKeyDeploymentsListTotalPages(state),
  totalElements: getKeyDeploymentsListTotalElements(state),
  results: getKeyDeploymentsListResults(state),
  userContext: getUserDetails(state),
});

const mapDispatchToProps = {
  fetchKeyDeploymentList,
  push,
};
ListRaw.displayName = 'KeyDeployments';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ListRaw);
